'use client';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { Controller } from 'react-hook-form';

import { FormField } from '../shared/FormField';
import { ValuePairFields } from '../shared/ValuePairFields';
import type { InventoryFormTypeProps } from '../types';
import { getFormItemInfo } from '../utils';
import { useFieldChanges } from '@/components/documents/ai-import/review-card/field-changes-context';

/**
 * How the deceased held the shares. The backend's ownership codes (VALE-1940):
 * this list stored its own title-case labels as the value until then, which the
 * AI field registry could never match.
 */
export const PRIVATE_COMPANY_OWNERSHIP_OPTIONS = [
  { value: 'solely-held', label: 'Solely owned' },
  { value: 'joint-tenants', label: 'Held jointly' },
  { value: 'tenants-in-common', label: 'Tenants in common' }
] as const;

/**
 * Private Company inventory form
 *
 * Direct shareholding in an Australian proprietary company (Pty Ltd) — typically
 * a family business or closely-held company. Not notifiable: no institution to
 * write to, lawyer enters details and valuation directly.
 *
 * Fields:
 * - Company Name
 * - ACN / ABN
 * - Number of Shares Held
 * - Held solely / jointly (ownershipType)
 * - Date of Death Value, Current Value
 * - Notes
 */
export function PrivateCompanyForm({
  form,
  mode,
  clickToCopy = false
}: InventoryFormTypeProps): ReactElement {
  const itemInfo = getFormItemInfo(form);

  const fieldChanges = useFieldChanges();
  const enrichment = (key: string) =>
    fieldChanges?.[key]?.changeType === 'enrichment'
      ? ('enrichment' as const)
      : undefined;

  if (mode === 'read') {
    const ownershipLabel = PRIVATE_COMPANY_OWNERSHIP_OPTIONS.find(
      (o) => o.value === itemInfo.ownershipType
    )?.label;

    return (
      <Fragment>
        {Boolean(itemInfo.companyName) && (
          <FormField
            label="Company Name"
            value={itemInfo.companyName as string}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {Boolean(itemInfo.acn) && (
          <FormField
            label="ACN"
            value={itemInfo.acn as string}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {Boolean(itemInfo.abn) && (
          <FormField
            label="ABN"
            value={itemInfo.abn as string}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {Boolean(itemInfo.numberOfShares) && (
          <FormField
            label="Number of Shares Held"
            value={(itemInfo.numberOfShares as number).toLocaleString()}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {Boolean(ownershipLabel) && (
          <FormField
            label="Held"
            value={ownershipLabel as string}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        <ValuePairFields
          mode="read"
          leftLabel="Date of Death Value"
          leftValue={itemInfo.valueAtDateOfDeath as number}
          rightLabel="Current Value"
          rightValue={itemInfo.currentValue as number}
          clickToCopy={clickToCopy}
          leftChangeType={enrichment('itemInfo.valueAtDateOfDeath')}
          rightChangeType={enrichment('itemInfo.currentValue')}
        />

        {Boolean(itemInfo.notes) && (
          <FormField
            label="Notes"
            value={itemInfo.notes as string}
            mode="read"
            multiline
            clickToCopy={clickToCopy}
          />
        )}
      </Fragment>
    );
  }

  // Edit mode
  return (
    <Fragment>
      <Controller
        control={form.control}
        name="itemInfo.companyName"
        render={({ field, fieldState }) => (
          <FormField
            label="Company Name"
            value={(field.value as string) || ''}
            mode="edit"
            placeholder="e.g. Smith Family Holdings Pty Ltd"
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.acn"
        render={({ field, fieldState }) => (
          <FormField
            label="ACN"
            value={(field.value as string) || ''}
            mode="edit"
            placeholder="123 456 789"
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.abn"
        render={({ field, fieldState }) => (
          <FormField
            label="ABN"
            value={(field.value as string) || ''}
            mode="edit"
            placeholder="12 345 678 901"
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.numberOfShares"
        render={({ field, fieldState }) => (
          <FormField
            label="Number of Shares Held"
            value={(field.value as number | undefined)?.toString() ?? ''}
            mode="edit"
            type="number"
            placeholder="0"
            onChange={(val) => {
              const parsed = val === '' ? undefined : Number(val);
              field.onChange(parsed);
            }}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.ownershipType"
        render={({ field, fieldState }) => (
          <FormField
            label="Held"
            value={(field.value as string) || ''}
            mode="edit"
            type="select"
            placeholder="Select ownership"
            options={PRIVATE_COMPANY_OWNERSHIP_OPTIONS}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.valueAtDateOfDeath"
        render={({ field: leftField, fieldState: leftState }) => (
          <Controller
            control={form.control}
            name="itemInfo.currentValue"
            render={({ field: rightField, fieldState: rightState }) => (
              <ValuePairFields
                mode="edit"
                leftLabel="Date of Death Value"
                leftValue={leftField.value as number}
                rightLabel="Current Value"
                rightValue={rightField.value as number}
                onLeftChange={leftField.onChange}
                onRightChange={rightField.onChange}
                leftPlaceholder="0.00"
                rightPlaceholder="0.00"
                leftError={leftState.error?.message}
                rightError={rightState.error?.message}
              />
            )}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.notes"
        render={({ field, fieldState }) => (
          <FormField
            label="Notes"
            value={(field.value as string) || ''}
            mode="edit"
            type="textarea"
            placeholder="Add your notes..."
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />
    </Fragment>
  );
}
